import { createRouter, createWebHashHistory } from "vue-router";
import { pageType } from "@/utils/point";
import { usePageTimeStore } from "@/stores/pageTime";
import { useUserStore } from "@/stores";
import { getUrlParams, removeUrlToken } from "@/utils";
import sdkBridge from "@/utils/sdkBridge";
import { useToastStore, GAME_ID } from "@/stores";
import { usePointStore } from "@/stores/point";
import i18n from "@/i18n";
import Home from "@/views/Home.vue";
import Notice from "@/views/Notice.vue";
import Profile from "@/views/Profile.vue";
import PostDetail from "@/views/PostDetail.vue";
import Post from "@/views/Post.vue";
import Search from "@/views/Search.vue";
import { supportedGameIds } from "@/config/gameConfig";
const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: "/",
      name: "home",
      component: Home,
    },
    {
      path: "/post/:id",
      name: "postDetail",
      component: PostDetail,
    },
    {
      path: "/notice",
      name: "notice",
      component: Notice,
    },
    {
      path: "/profile",
      name: "profile",
      component: Profile,
    },
    {
      path: "/post",
      name: "post",
      component: Post,
    },
    {
      path: "/search",
      name: "search",
      component: Search,
    },
    {
      path: "/nickname/modify",
      name: "nicknameModify",
      component: () => import("../views/NicknameModify.vue"),
    },
    {
      path: "/nickname/modify-record",
      name: "nicknameModifyRecord",
      component: () => import("../views/NicknameModifyRecord.vue"),
    },
    {
      path: "/shop",
      name: "shop",
      component: () => import("../views/Shop.vue"),
    },
    {
      path: "/exchange-record",
      name: "exchangeRecord",
      component: () => import("../views/ExchangeRecord.vue"),
    },
    {
      path: "/setting",
      name: "setting",
      component: () => import("../views/Setting.vue"),
    },
    {
      path: "/fans/:id?",
      name: "fans",
      component: () => import("../views/Fans.vue"),
    },
    {
      path: "/follow/:id?",
      name: "follow",
      component: () => import("../views/Follow.vue"),
    },
    {
      path: "/avatar",
      name: "avatar",
      component: () => import("../views/AvatarSelect.vue"),
    },
    {
      path: "/user/:uid",
      name: "user",
      component: () => import("../views/User.vue"),
    },
    {
      path: "/select-board",
      name: "selectBoard",
      component: () => import("../views/SelectBoard.vue"),
    },
    {
      path: "/reply/:id",
      name: "replyDetail",
      component: () => import("@/views/ReplyDetail.vue"),
    },
    {
      path: "/maintenance",
      name: "maintenance",
      component: () => import("@/views/Maintenance.vue"),
    },
    {
      path: "/page",
      name: "page",
      component: () => import("@/views/Page.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/",
    },
  ],
});

// 初始化用户状态
let isUserInitialized = false;
export const initializeUserState = async () => {
  if (isUserInitialized) return;

  const userStore = useUserStore();
  const toastStore = useToastStore();
  const { t } = i18n.global;

  // 1. 处理URL中的token登录
  const token = getUrlParams("token");
  if (token) {
    const res = await userStore.login(token);
    removeUrlToken();
    if (res?.code === 200) {
      toastStore.showToast(t("home.loginInSuccess"), "success");
      // 登录成功后移除URL中的token
    } else {
      // 2025-12-22 金添： 100001 为gpassport使用的TOKEN情况，微端会携带，不显示错误提示，清空token即可
      if (res?.code !== 100001) {
        toastStore.showErrorCodeToast(res?.code, t);
      }
    }
  }

  // 2. 获取用户信息并检测登录状态
  try {
    const res = await userStore.getUserInfo();
    // webview 内支持中的游戏若无登录态，则跳转到登录页
    if (
      +res.code !== 200 &&
      sdkBridge.runInsdk() &&
      supportedGameIds.includes(GAME_ID)
    ) {
      userStore.checkLogin();
    }
    if (res.code === 200) {
      // 如果有登录态，初始化任务列表以启动浏览时长定时器
      const pointStore = usePointStore();
      await pointStore.getTasks();
    }
  } catch (error) {
    console.error("Failed to get user info:", error);
  }

  isUserInitialized = true;
};

// 添加路由守卫（maintenance 跳转由 main.ts 在挂载前根据 hasGameConfig 统一处理）
router.beforeEach(async (to, from, next) => {
  const pageTimeStore = usePageTimeStore();
  // 记录页面访问
  const currentPageType = to.name || pageType.HOME;

  // 如果路由有参数（params 或 query），合并后上报
  let eventValue: string | undefined;
  const hasParams = to.params && Object.keys(to.params).length > 0;
  const hasQuery = to.query && Object.keys(to.query).length > 0;

  if (hasParams || hasQuery) {
    const allParams: Record<string, any> = {};
    if (hasParams) Object.assign(allParams, to.params);
    if (hasQuery) Object.assign(allParams, to.query);
    eventValue = JSON.stringify(allParams);
  }

  pageTimeStore.enterPage(currentPageType, eventValue);

  next();
});

// 添加页面可见性监听，页面隐藏时暂停计时，显示时恢复
document.addEventListener("visibilitychange", () => {
  const pageTimeStore = usePageTimeStore();
  if (document.hidden) {
    // 页面隐藏，暂停计时
    pageTimeStore.pauseTimer();
  } else {
    // 页面显示，恢复计时
    pageTimeStore.resumeTimer();
  }
});

export default router;
